import { Button, Card, Space, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useContext, useEffect, useState } from "react";
import {
  ActivityProps,
  CustomerProps,
  TagProps,
} from "../../interfaces/interfaces";
import dayjs from "dayjs";
import {
  EditOutlined,
  CopyOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  IssuesCloseOutlined,
  PlusOutlined,
  UpOutlined,
  DeleteOutlined,
  DownOutlined,
} from "@ant-design/icons";
import DataContext from "../../context/DataContext";
import { TablePaginationConfig } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import Link from "antd/es/typography/Link";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const ActivityList = ({
  origin,
  onChangePage,
  onAddNewActivity,
  onEditActivity,
  onDuplicateActivity,
  onSelectCustomer,
  onDeleteActivity,
}: {
  origin?: "customer" | "project" | "dashboard";
  onChangePage: (current: number, pageSize: number) => void;
  onAddNewActivity: () => void;
  onEditActivity: (values: ActivityProps) => void;
  onDuplicateActivity: (values: ActivityProps) => void;
  onSelectCustomer?: (customer: string) => void;
  onDeleteActivity?: (
    values: ActivityProps,
    pageSize: number,
    currentPage: number,
  ) => void;
}) => {
  const { dataLoading, activityList, getData } = useContext(DataContext);
  const [tableParams, setTableParams] = useState<TableParams>({});
  const [pageSize, setPageSize] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [cardBodyVisible, setCardBodyVisible] = useState(
    origin === "customer" || origin === "project",
  );

  useEffect(() => {
    onChangePage(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    setTableParams({
      pagination: {
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Activities`,
        position: ["bottomCenter"],
        total: parseInt(activityList.pagination?.total) || 0,
        onChange: (page, pageSize) => {
          setCurrentPage(page);
          setTableParams((prev) => {
            return {
              ...prev,
              pagination: {
                ...prev.pagination,
                pageSize: pageSize,
              },
            };
          });
          setPageSize(pageSize);
        },
      },
    });
  }, [activityList.pagination]);

  const activityColumns: ColumnsType<ActivityProps> = [
    {
      title: "Actions",
      key: "edit",
      render: (params) => (
        <Space>
          <Tooltip title="Edit" placement="top">
            <Button
              onClick={() => onEditActivity(params)}
              icon={<EditOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          <Tooltip title="Duplicate" placement="top">
            <Button
              onClick={() => onDuplicateActivity(params)}
              icon={<CopyOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          {onDeleteActivity && (
            <Tooltip title="Delete" placement="right">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() => onDeleteActivity(params, pageSize, currentPage)}
                icon={<DeleteOutlined />}
                size="small"
              ></Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "typeOfTask",
      key: "typeOfTask",
      render: (typeOfTask: ActivityProps["typeOfTask"]) =>
        typeOfTask === "Call" ? (
          <>
            <Tag color="lime" icon={<PhoneOutlined />}>
              Call
            </Tag>
          </>
        ) : typeOfTask === "Email" ? (
          <>
            <Tag color="cyan" icon={<MailOutlined />}>
              Email
            </Tag>
          </>
        ) : typeOfTask === "Follow-up" ? (
          <>
            <Tag color="magenta" icon={<CheckCircleOutlined />}>
              Follow-up
            </Tag>
          </>
        ) : typeOfTask === "Meeting" ? (
          <>
            <Tag color="geekblue" icon={<TeamOutlined />}>
              Meeting
            </Tag>
          </>
        ) : (
          <Tag color="orange" icon={<IssuesCloseOutlined />}>
            {typeOfTask}
          </Tag>
        ),
      sorter: (a, b) => a.typeOfTask.localeCompare(b.typeOfTask),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 200,
      render: (customer: CustomerProps) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: 180,
          }}
        >
          {customer && customer._id && (
            <div style={{ minWidth: "120px" }}>
              {onSelectCustomer ? (
                <Link>{customer?.DisplayName || ""}</Link>
              ) : (
                <div>{customer?.DisplayName || ""}</div>
              )}
            </div>
          )}
        </div>
      ),
      onCell: (record) => {
        return {
          onClick: () =>
            record.customer?._id && onSelectCustomer?.(record.customer?._id),
        };
      },
      hidden: origin === "customer",
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      render: (project) => (
        <Tooltip title={project?.name || "-"}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              maxWidth: 180,
            }}
          >
            {project?.name || "-"}
          </div>
        </Tooltip>
      ),
      responsive: ["xl", "lg"],
      sorter: (a, b) => {
        const nameA = a.project?.name || "";
        const nameB = b.project?.name || "";
        return nameA.localeCompare(nameB);
      },
      hidden: origin === "customer",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: 180,
          }}
        >
          {description}
        </div>
      ),
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Start",
      dataIndex: "startDate",
      className: "align-center",
      key: "startDate",
      render: (startDate: string) =>
        startDate ? dayjs(startDate).format("MM/DD/YYYY HH:mm") : "-",
      sorter: (a, b) =>
        (a.startDate
          ? dayjs(a.startDate).format("YYYY-MM-DD")
          : ""
        ).localeCompare(
          b.startDate ? dayjs(b.startDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "End",
      dataIndex: "endDate",
      className: "align-center",
      key: "endDate",
      render: (endDate: string) =>
        endDate ? dayjs(endDate).format("MM/DD/YYYY HH:mm") : "-",
      sorter: (a, b) =>
        (a.endDate ? dayjs(a.endDate).format("YYYY-MM-DD") : "").localeCompare(
          b.endDate ? dayjs(b.endDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Duration",
      dataIndex: "duration",
      className: "align-center",
      render: (_, row) => {
        if (!row.startDate || !row.endDate) return "-";
        const start = dayjs(row.startDate);
        const end = dayjs(row.endDate);
        const duration = dayjs.duration(end.diff(start));

        const days = duration.days();
        const hours = duration.hours();
        const minutes = duration.minutes();

        return days
          ? `${days}d ${hours}h ${minutes}m `
          : hours
            ? `${hours}h ${minutes}m `
            : minutes
              ? `${minutes}m`
              : "-";
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority: string) => (
        <Tag
          color={
            priority === "High"
              ? "red"
              : priority === "Medium"
                ? "orange"
                : "green"
          }
        >
          {priority}
        </Tag>
      ),
      sorter: (a, b) => a.priority.localeCompare(b.priority),
      responsive: ["xl", "lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status === "Planned"
              ? "red"
              : status === "In Progress"
                ? "gold"
                : "green"
          }
        >
          {status}
        </Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      responsive: ["xl", "lg"],
    },
    {
      title: "Tags",
      dataIndex: "tags",
      width: "20%",
      key: "tags",
      render: (tags: TagProps[]) => (
        <div>
          {tags.map((tag: TagProps) => (
            <Tag key={tag._id} color={tag.color} bordered={false}>
              {tag.name}
            </Tag>
          ))}
        </div>
      ),
      responsive: ["xl", "lg"],
    },
    {
      title: "Assigned",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (_, row) => (
        <>
          {Array.isArray(row.assignedTo) ? (
            row.assignedTo?.map((assigned) => (
              <div
                key={assigned._id}
                color="blue"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  maxWidth: 180,
                }}
              >
                {assigned?.name}
              </div>
            ))
          ) : (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                maxWidth: 180,
              }}
            >
              {row.assignedTo?.name}
            </div>
          )}
        </>
      ),
      responsive: ["xl", "lg", "md"],
      hidden: origin === "dashboard",
    },
  ];
  return (
    <Card
      hoverable
      title={
        <Space>
          <div>Activities</div>
          <Tooltip title="New">
            <Button icon={<PlusOutlined />} onClick={onAddNewActivity}></Button>
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 16 }}
      extra={
        <Button
          type="link"
          icon={cardBodyVisible ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setCardBodyVisible((v) => !v)}
        >
          {cardBodyVisible ? "Close Activities" : "Open Activities"}
        </Button>
      }
    >
      {cardBodyVisible && (
        <Table
          loading={dataLoading}
          columns={activityColumns}
          dataSource={activityList.data}
          pagination={tableParams.pagination}
          rowKey="_id"
          size="small"
          tableLayout="auto"
          style={{ width: "100%", overflowX: "scroll", minHeight: "200px" }}
          locale={{ emptyText: "No Activities " }}
        />
      )}
    </Card>
  );
};
